//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import React, { ReactChild } from 'react';
import styled from 'styled-components';

import OverlayIcon from '@components/atoms/overlay-icon/OverlayIcon';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  rightsStatus: RIGHTS_STATUS;
  tooltipContentWrapper?: (msg: RightsIconMessages) => ReactChild;
  isRejectedForMissingTerms?: boolean;
}

enum RightsIconMessages {
  APPROVED = 'The user has approved rights to your image.',
  PENDING = 'Your rights request is pending approval by the user.',
  REJECTED = 'Your rights request expired before the user approved it.',
  FAILED = 'Failed to send rights request. Please try again.',
  REJECTED_MISSING_TERMS = 'Your rights request did not include the required T&Cs URL.',
}

export const RightsOverlayIcon = ({
  className,
  isRejectedForMissingTerms,
  rightsStatus,
  tooltipContentWrapper,
}: Props) => {
  const contentWrapper = tooltipContentWrapper ?? ((msg) => msg);

  // If the rights request didn't contain a T&C URL and the client is on the pilot
  // (feature flag enabled) we want to switch the error message of the tool tip
  // to REJECTED_MISSING_TERMS
  if (isRejectedForMissingTerms) {
    return (
      <RightsRejectedIcon
        className={className}
        tooltipContent={contentWrapper(
          RightsIconMessages.REJECTED_MISSING_TERMS,
        )}
      />
    );
  }

  switch (rightsStatus.toLowerCase()) {
    case RIGHTS_STATUS.APPROVED.toLowerCase(): {
      return (
        <RightsApprovedIcon
          className={className}
          tooltipContent={contentWrapper(RightsIconMessages.APPROVED)}
        />
      );
    }
    case RIGHTS_STATUS.INITIATED.toLowerCase():
    case RIGHTS_STATUS.PENDING.toLowerCase(): {
      return (
        <RightsPendingIcon
          className={className}
          tooltipContent={contentWrapper(RightsIconMessages.PENDING)}
        />
      );
    }
    case RIGHTS_STATUS.EXPIRED.toLowerCase(): {
      return (
        <RightsRejectedIcon
          className={className}
          tooltipContent={contentWrapper(RightsIconMessages.REJECTED)}
        />
      );
    }
    case RIGHTS_STATUS.FAILED_TO_SEND.toLowerCase(): {
      return (
        <RightsRejectedIcon
          className={className}
          tooltipContent={contentWrapper(RightsIconMessages.FAILED)}
        />
      );
    }
    default: {
      return null;
    }
  }
};

const RightsApprovedIcon = styled(OverlayIcon).attrs({
  iconName: 'request-rights-approved',
})`
  color: ${({ theme }) => theme.colors.green5};
`;

const RightsPendingIcon = styled(OverlayIcon).attrs({
  iconName: 'request-rights-pending',
})`
  color: ${({ theme }) => theme.colors.yellow2};
`;

const RightsRejectedIcon = styled(OverlayIcon).attrs({
  iconName: 'request-rights-rejected',
})`
  color: ${({ theme }) => theme.colors.red5};
`;

export default RightsOverlayIcon;
